import type { AssinaturaOcupacao } from './tipos';

/**
 * A conta da ocupação consolidada, para QUALQUER recorte de centros.
 *
 * A API não manda totais prontos, manda assinaturas: cada posição física
 * reduzida a "em que centros existo, em quais estou ocupada, em quais estou
 * negativa", com as iguais agrupadas (12.700 endereços da empresa 22 viram
 * ~140 linhas). Daqui sai qualquer combinação instantaneamente, sem uma ida ao
 * servidor por clique.
 *
 * UMA MÉTRICA SÓ: **endereço físico**, contado uma vez.
 *
 * Houve uma versão com dois totais — endereços e "posições" (o par
 * centro+endereço, que conta o mesmo lugar uma vez por centro). Dois números
 * grandes lado a lado, ambos chamados de ocupação, e ninguém sabia qual levar
 * para a reunião. Ficou o endereço, que é o que responde "quanto do galpão
 * está cheio" e é a mesma pergunta que a dashboard do Superset faz na métrica
 * "Localizadores" — assim as duas telas falam do mesmo objeto, e a diferença
 * que sobrar é de regra, não de definição.
 *
 * Repare que DENTRO de um centro os dois números são iguais: um localizador
 * aparece uma vez só. A diferença só nasce ao somar centros, porque a mesma
 * rua guarda venda, open box e reserva ao mesmo tempo. Por isso a linha de
 * cada centro continua exata, e só o total mudou de definição.
 *
 * O produto virtual (ebook, seção 943) já sai excluído do saldo lá na API:
 * ele não ocupa prateleira e o razão o debita espalhado por posições reais.
 */
export interface ResumoOcupacaoEmpresa {
  /** Endereços físicos distintos nos centros marcados. */
  enderecos: number;
  enderecosOcupados: number;
  enderecosLivres: number;
  /** Endereços com saldo negativo em pelo menos um centro marcado. */
  enderecosNegativos: number;
  percentualOcupacao: number;
}

export interface TotaisCentro {
  posicoesTotal: number;
  posicoesOcupadas: number;
  posicoesNegativas: number;
  percentualOcupacao: number;
}

function pct(parte: number, todo: number): number {
  return todo > 0 ? Math.round((parte / todo) * 1000) / 10 : 0;
}

/** Se algum item de `lista` está na seleção. */
function tocaSelecao(lista: readonly number[], selecionados: ReadonlySet<number>): boolean {
  for (const c of lista) if (selecionados.has(c)) return true;
  return false;
}

export function resumirOcupacao(
  assinaturas: readonly AssinaturaOcupacao[],
  selecionados: ReadonlySet<number>,
): ResumoOcupacaoEmpresa {
  let enderecos = 0;
  let enderecosOcupados = 0;
  let enderecosNegativos = 0;

  for (const a of assinaturas) {
    // O endereço entra na conta uma vez, mesmo servindo a três centros
    // marcados — é justamente isso que a métrica quer dizer.
    if (!tocaSelecao(a.centros, selecionados)) continue;

    enderecos += a.quantidade;
    if (tocaSelecao(a.ocupados, selecionados)) enderecosOcupados += a.quantidade;
    if (tocaSelecao(a.negativos, selecionados)) enderecosNegativos += a.quantidade;
  }

  return {
    enderecos,
    enderecosOcupados,
    enderecosLivres: Math.max(0, enderecos - enderecosOcupados),
    enderecosNegativos,
    percentualOcupacao: pct(enderecosOcupados, enderecos),
  };
}

/**
 * Os totais de cada centro, independentes da seleção.
 *
 * A linha do centro mostra sempre o que aquele centro tem — desmarcar tira do
 * total, não zera a linha. Esconder o número do centro desmarcado obrigaria a
 * marcar de novo só para lembrar por que ele foi desmarcado.
 */
export function totaisPorCentro(
  assinaturas: readonly AssinaturaOcupacao[],
): Map<number, TotaisCentro> {
  const mapa = new Map<number, TotaisCentro>();
  const pegar = (centro: number): TotaisCentro => {
    const atual = mapa.get(centro);
    if (atual) return atual;
    const novo = {
      posicoesTotal: 0,
      posicoesOcupadas: 0,
      posicoesNegativas: 0,
      percentualOcupacao: 0,
    };
    mapa.set(centro, novo);
    return novo;
  };

  for (const a of assinaturas) {
    for (const c of a.centros) pegar(c).posicoesTotal += a.quantidade;
    for (const c of a.ocupados) pegar(c).posicoesOcupadas += a.quantidade;
    for (const c of a.negativos) pegar(c).posicoesNegativas += a.quantidade;
  }
  for (const t of mapa.values()) {
    t.percentualOcupacao = pct(t.posicoesOcupadas, t.posicoesTotal);
  }
  return mapa;
}
